import type { OfflineState } from "../pwa/updates";
import { buttonView, INITIAL_STATUS, type Phase, type UpdateStatus } from "../pwa/updateFlow";

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "3 days ago", "just now"... */
function formatAge(iso: string, now = Date.now()): string {
  const seconds = Math.round((Date.parse(iso) - now) / 1000);
  if (Number.isNaN(seconds)) return "";
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

const OFFLINE_TEXT: Record<OfflineState, string> = {
  ready: "Works offline",
  unavailable: "Offline mode unavailable",
};

/**
 * Footer on the home screen: running version, its age, offline state, and the
 * update button ("Check for updates" / "Update now"), always visible.
 * `onPress` gets the phase the button was in; src/main.ts decides what to run.
 */
export class VersionFooter {
  readonly element: HTMLElement;
  private readonly offlineEl: HTMLSpanElement;
  private readonly button: HTMLButtonElement;
  private readonly note: HTMLSpanElement;

  constructor(onPress: (phase: Phase) => void, onDiagnostics?: () => void, onAdvanced?: () => void) {
    this.element = document.createElement("footer");
    this.element.className = "app-footer";
    const released = new Date(__APP_RELEASED_AT__);
    this.element.innerHTML = `
      <span class="version"></span>
      <span class="update-state" role="status"></span>
      <div class="update-row">
        <button type="button" class="btn update-btn" data-action="update"></button>
        <span class="update-note error" role="alert"></span>
        <span class="update-tools">
          <button type="button" class="btn diag-link" data-action="diagnostics">Diagnostics</button>
          <button type="button" class="btn adv-link" data-action="advanced">Advanced</button>
        </span>
      </div>
    `;
    const versionEl = this.element.querySelector(".version") as HTMLSpanElement;
    const age = formatAge(__APP_RELEASED_AT__);
    versionEl.textContent = `v${__APP_VERSION__}${import.meta.env.DEV ? "-dev" : ""}` + (age ? ` · released ${age}` : "");
    if (!Number.isNaN(released.getTime())) versionEl.title = released.toLocaleString();

    this.offlineEl = this.element.querySelector(".update-state") as HTMLSpanElement;
    this.button = this.element.querySelector('[data-action="update"]') as HTMLButtonElement;
    this.note = this.element.querySelector(".update-note") as HTMLSpanElement;
    this.button.addEventListener("click", () => onPress(this.button.dataset.phase as Phase));
    const diagnostics = this.element.querySelector('[data-action="diagnostics"]') as HTMLButtonElement;
    if (onDiagnostics) diagnostics.addEventListener("click", onDiagnostics);
    else diagnostics.hidden = true;
    const advanced = this.element.querySelector('[data-action="advanced"]') as HTMLButtonElement;
    if (onAdvanced) advanced.addEventListener("click", onAdvanced);
    else advanced.hidden = true;
    this.setStatus(INITIAL_STATUS);
  }

  setOfflineState(state: OfflineState): void {
    this.offlineEl.textContent = `· ${OFFLINE_TEXT[state]}`;
    this.offlineEl.classList.toggle("error", state === "unavailable");
  }

  setStatus(status: UpdateStatus): void {
    const view = buttonView(status, __APP_VERSION__);
    this.button.textContent = view.label;
    this.button.disabled = view.disabled;
    this.button.dataset.phase = status.phase;
    this.button.classList.toggle("primary", status.phase === "ready");
    this.button.classList.toggle("busy", status.phase === "checking" || status.phase === "applying");
    this.note.textContent = view.note;
    this.note.hidden = !view.note;
  }

  /** Browsers without service workers cannot update: no button. */
  hideUpdateButton(): void {
    this.button.hidden = true;
  }
}
